import { USStateOptions } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { DetailLabel } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Tag } from '@strata/react-tags';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import classNames from 'classnames';
import { FieldArray, Formik, getIn } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import { handleError, useLastErrorDialog } from 'services/ErrorManagement';
import { get, put } from 'services/Http';

const StateCapabilitySettings = {
    ALL_STATES: 'All States',
    SPECIFIC_STATES: 'Specific States',
};

export const StateCapabilityTab = ({ onSecondaryButtonClick = undefined }) => {
    const { npi } = useParams();

    const { ErrorDialogComponent: ErrorDialog, parseAndSetLastError } = useLastErrorDialog();

    const [existingCapabilities, setExistingCapabilities] = useState();
    useEffect(() => {
        void (async () => {
            try {
                setExistingCapabilities(await get(`/reference/capabilities/${npi}`));
            } catch (err) {
                handleError('Error fetching state capabilities', err);
            }
        })();
    }, [npi]);

    const mappedCapabilities = (existingCapabilities?.state_capabilities ?? []).map(({ state }) =>
        USStateOptions.find(({ secondaryData }) => secondaryData === state),
    );

    return (
        <Formik
            initialValues={{
                stateCapabilitySettings:
                    USStateOptions.length === mappedCapabilities.length
                        ? StateCapabilitySettings.ALL_STATES
                        : StateCapabilitySettings.SPECIFIC_STATES,
                stateCapabilityList: mappedCapabilities,
                statesToRemoveList: [],
            }}
            validationSchema={Yup.object({
                stateCapabilitySettings: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                stateCapabilityList: Yup.array().required(
                    validationStrings.requiredField.errorMessage,
                ),
                statesToRemoveList: Yup.array().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            enableReinitialize
            onSubmit={async (values) => {
                try {
                    const body = {
                        supplier_npi: npi,
                        cap_type: 'state',
                        caps_to_add: (values.stateCapabilitySettings ===
                        StateCapabilitySettings.ALL_STATES
                            ? USStateOptions
                            : values.stateCapabilityList
                        ).map(({ secondaryData }) => secondaryData),
                        caps_to_remove: values.statesToRemoveList.map(
                            ({ secondaryData }) => secondaryData,
                        ),
                    };

                    await put(`/reference/capabilities/${npi}`, body);

                    await SuccessToast({
                        message: 'State capabilities successfully updated.',
                    });

                    setExistingCapabilities(await get(`/reference/capabilities/${npi}`));
                } catch (err) {
                    parseAndSetLastError(err);
                }
            }}
        >
            {(formik) => {
                const stateToAdd = getIn(formik.values, 'stateCapability');
                const clearAllStates = () => {
                    formik.setValues({
                        ...formik.values,
                        stateCapabilitySettings: StateCapabilitySettings.SPECIFIC_STATES,
                        stateCapability: undefined,
                        stateCapabilityList: [],
                        statesToRemoveList: formik.initialValues.stateCapabilityList ?? [],
                    });
                };
                const canClearAll =
                    formik.values.stateCapabilitySettings === StateCapabilitySettings.ALL_STATES ||
                    formik.values.stateCapabilityList.length > 0;

                return (
                    <>
                        <ErrorDialog />
                        <FormContainer
                            formTitle="Edit State Capabilities"
                            isLoading={!existingCapabilities}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={!(formik.dirty && formik.isValid) || formik.isSubmitting}
                            onSecondaryButtonClick={onSecondaryButtonClick ?? formik.handleReset}
                            formLeftContent={
                                <>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <RadioButtonGroupFormik
                                            name="stateCapabilitySettings"
                                            options={[
                                                StateCapabilitySettings.ALL_STATES,
                                                StateCapabilitySettings.SPECIFIC_STATES,
                                            ]}
                                            inline
                                        />
                                        <ActionButtonText
                                            label="Clear All"
                                            disabled={!canClearAll}
                                            onClick={clearAllStates}
                                        />
                                    </div>
                                    {formik.values.stateCapabilitySettings ===
                                        StateCapabilitySettings.SPECIFIC_STATES && (
                                        <>
                                            <div style={{ display: 'flex' }}>
                                                <SuggestSingleSelectFormik
                                                    id="stateCapability"
                                                    label="State"
                                                    helperText="A state that the supplier can service in"
                                                    required
                                                    disableAutoPopulate
                                                    values={USStateOptions.filter(
                                                        (option) =>
                                                            !formik.values.stateCapabilityList.find(
                                                                ({ secondaryData }) =>
                                                                    secondaryData ===
                                                                    option.secondaryData,
                                                            ),
                                                    )}
                                                />
                                                <Button
                                                    label="Add State"
                                                    className="sds_form-button__center-with-inputs margin__left--medium"
                                                    disabled={!stateToAdd}
                                                    intent={Button.Intent.PRIMARY}
                                                    onClick={() => {
                                                        formik.setValues({
                                                            ...formik.values,
                                                            stateCapability: undefined,
                                                            stateCapabilityList: [
                                                                ...formik.values
                                                                    .stateCapabilityList,
                                                                stateToAdd,
                                                            ],
                                                            statesToRemoveList:
                                                                formik.values.statesToRemoveList.filter(
                                                                    (stateCapability) =>
                                                                        stateCapability.secondaryData !==
                                                                        stateToAdd?.secondaryData,
                                                                ),
                                                        });
                                                    }}
                                                />
                                            </div>
                                            <DetailLabel label="Selected States" />
                                            <FieldArray name="stateCapabilityList">
                                                {(fieldArray) => (
                                                    <div className="sds_capabilities">
                                                        {formik.values.stateCapabilityList
                                                            .sort((a, b) => sortAsc(a.name, b.name))
                                                            .map((stateCapability, index) => (
                                                                <Tag
                                                                    key={
                                                                        stateCapability.secondaryData
                                                                    }
                                                                    className={classNames(
                                                                        'margin__bottom--medium',
                                                                        'sds_capabilities__tag',
                                                                    )}
                                                                    label={`${stateCapability.name} - ${stateCapability.secondaryData}`}
                                                                    onRemove={async () => {
                                                                        const wasInInitialValues =
                                                                            formik.initialValues.stateCapabilityList.find(
                                                                                ({
                                                                                    secondaryData,
                                                                                }) =>
                                                                                    secondaryData ===
                                                                                    stateCapability.secondaryData,
                                                                            );
                                                                        if (wasInInitialValues) {
                                                                            await formik.setFieldValue(
                                                                                'statesToRemoveList',
                                                                                [
                                                                                    ...formik.values
                                                                                        .statesToRemoveList,
                                                                                    stateCapability,
                                                                                ],
                                                                            );
                                                                        }
                                                                        fieldArray.remove(index);
                                                                    }}
                                                                />
                                                            ))}
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </>
                                    )}
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};

StateCapabilityTab.propTypes = {
    onSecondaryButtonClick: PropTypes.func,
};
